import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscriptionService, SubscriptionStatus } from '../api/subscription';
import { useAuth } from '../hooks/useAuth';

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
  hasAccess: (feature: string) => boolean;
  canUseFeature: (feature: string, increment?: number) => boolean;
  getTier: () => 'free' | 'plus' | 'pro';
  isActiveSubscriber: () => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated()) {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const status = await subscriptionService.getCurrentSubscription(getAuthHeaders());
      setSubscriptionStatus(status);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError('Failed to load subscription status');
      // Default to free tier on error
      setSubscriptionStatus({
        has_subscription: false,
        usage_limits: {
          tier: 'free',
          limits: {
            ai_chat_sessions: 5,
            assessments: 'unlimited',
            custom_learning_paths: 0
          },
          current_usage: {},
          period_start: new Date().toISOString(),
          period_end: new Date().toISOString()
        }
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const getTier = useCallback((): 'free' | 'plus' | 'pro' => {
    if (!subscriptionStatus) return 'free';
    return (subscriptionStatus.subscription?.tier || subscriptionStatus.usage_limits?.tier || 'free') as 'free' | 'plus' | 'pro';
  }, [subscriptionStatus]);

  const isActiveSubscriber = useCallback((): boolean => {
    return subscriptionStatus?.has_subscription || false;
  }, [subscriptionStatus]);

  const hasAccess = useCallback((feature: string): boolean => {
    if (!subscriptionStatus?.usage_limits) return false;
    
    const limit = subscriptionStatus.usage_limits.limits[feature];
    
    // Check if feature is available
    if (limit === 0) return false;
    if (limit === -1 || limit === 'unlimited') return true;
    
    // For numeric limits, check if user has any allowance
    return typeof limit === 'number' && limit > 0;
  }, [subscriptionStatus]);

  const canUseFeature = useCallback((feature: string, increment: number = 1): boolean => {
    if (!subscriptionStatus?.usage_limits) return false;
    
    const limit = subscriptionStatus.usage_limits.limits[feature];
    const currentUsage = subscriptionStatus.usage_limits.current_usage[feature] || 0;
    
    // Check unlimited features
    if (limit === -1 || limit === 'unlimited') return true;
    
    // Check if feature is available and within limits
    if (typeof limit === 'number') {
      return currentUsage + increment <= limit;
    }
    
    return false;
  }, [subscriptionStatus]);

  const value: SubscriptionContextType = {
    subscriptionStatus,
    loading,
    error,
    refreshSubscription,
    hasAccess,
    canUseFeature,
    getTier,
    isActiveSubscriber
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};